import { PlusOutlined } from '@ant-design/icons';
import { Button, Divider, Tooltip } from 'antd';
import { useMemo } from 'react';

import {
    SAVE_CURRENT_VIEW_TOOLTIP,
    SAVE_VIEW_LIMIT_TOOLTIP,
    SAVE_VIEW_TOOLTIP_PROPS,
} from '../savedViewStateSelect.constants';
import type { SavedViewsListDropdownProps } from '../SavedViewStateSelect.types';
import SavedViewListItem from './SavedViewListItem';
import SavedViewsEmptyState from './SavedViewsEmptyState';

const SavedViewsListDropdown = ({
    savedViews,
    expandedViewId,
    activeViewId,
    activeDataSource,
    actionLoading,
    canSaveCurrentView,
    isSaveLimitReached,
    onSelectSavedView,
    onOpenSaveViewForm,
    onLoadView,
    onDeleteView,
    onSetDefaultView,
    onRenameView,
    onUpdateLoadedView,
    onExitView,
    isViewModified,
    showModifiedIndicator,
}: SavedViewsListDropdownProps) => {
    const isActiveSavedView = (viewId: number) =>
        activeDataSource === 'view' && activeViewId === viewId;
    const systemViews = useMemo(() => savedViews.filter((view) => view.view_type === 'SYSTEM'), [savedViews]);
    const userCreatedViews = useMemo(() => savedViews.filter((view) => view.view_type === 'CUSTOM'), [savedViews]);
    const existingViewNames = useMemo(() => savedViews.map((view) => view.view_name), [savedViews]);

    return (
        <>
            {savedViews.length ? (
                <>
                    <div className="px-3 pb-2 text-[12px] font-bold leading-[16px] text-[#44338a]">
                        VIEWS ({savedViews.length})
                    </div>
                    <div className="flex flex-col gap-0">
                        {systemViews.map((savedView) => (
                            <SavedViewListItem
                                key={savedView.view_id}
                                savedView={savedView}
                                existingViewNames={existingViewNames}
                                isExpanded={expandedViewId === savedView.view_id}
                                loadingAction={
                                    actionLoading?.viewId === savedView.view_id ? actionLoading.action : null
                                }
                                isActiveView={isActiveSavedView(savedView.view_id)}
                                isViewModified={isActiveSavedView(savedView.view_id) && isViewModified}
                                showModifiedIndicator={isActiveSavedView(savedView.view_id) && showModifiedIndicator}
                                onSelect={onSelectSavedView}
                                onLoad={onLoadView}
                                onDelete={onDeleteView}
                                onSetDefault={onSetDefaultView}
                                onRename={onRenameView}
                                onUpdateView={onUpdateLoadedView}
                                onExitView={onExitView}
                            />
                        ))}
                        {systemViews.length > 0 && userCreatedViews.length > 0 ? (
                            <Divider className="!my-0 !border-borderLinePurpuleAlt" />
                        ) : null}
                        {userCreatedViews.map((savedView) => (
                            <SavedViewListItem
                                key={savedView.view_id}
                                savedView={savedView}
                                existingViewNames={existingViewNames}
                                isExpanded={expandedViewId === savedView.view_id}
                                loadingAction={
                                    actionLoading?.viewId === savedView.view_id ? actionLoading.action : null
                                }
                                isActiveView={isActiveSavedView(savedView.view_id)}
                                isViewModified={isActiveSavedView(savedView.view_id) && isViewModified}
                                showModifiedIndicator={isActiveSavedView(savedView.view_id) && showModifiedIndicator}
                                onSelect={onSelectSavedView}
                                onLoad={onLoadView}
                                onDelete={onDeleteView}
                                onSetDefault={onSetDefaultView}
                                onRename={onRenameView}
                                onUpdateView={onUpdateLoadedView}
                                onExitView={onExitView}
                            />
                        ))}
                    </div>
                    <Divider className="!my-2 !border-borderLinePurpuleAlt" />
                </>
            ) : (
                <SavedViewsEmptyState className="px-3 pb-3" />
            )}
            <div className="px-3">
                <Tooltip
                    {...SAVE_VIEW_TOOLTIP_PROPS}
                    title={isSaveLimitReached ? SAVE_VIEW_LIMIT_TOOLTIP : SAVE_CURRENT_VIEW_TOOLTIP}
                >
                    <span className="block">
                        <Button
                            className={`toolbar-btn !flex !h-[32px] !w-full !items-center !justify-center !gap-[6px] !rounded-[6px] !px-[15px] !text-[14px] !font-normal !text-[#44338a] ${!canSaveCurrentView ? 'opacity-50 !text-black/50' : ''}`}
                            onClick={onOpenSaveViewForm}
                            disabled={!canSaveCurrentView}
                        >
                            <PlusOutlined />
                            Save Current View
                        </Button>
                    </span>
                </Tooltip>
            </div>
        </>
    );
};

export default SavedViewsListDropdown;
